import { homeRoute } from './utils/homeRoute';
import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from './contexts/AuthContext';
import { useTheme } from './contexts/ThemeContext';
import { useMediaQuery } from './hooks/useMediaQuery';
import Login from './pages/Login';
import PrivateRoute from './components/PrivateRoute';

const ReportCatalog = lazy(() => import('./pages/ReportCatalog'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Home = lazy(() => import('./pages/Home'));
const MyDay = lazy(() => import('./pages/MyDay'));
const Map = lazy(() => import('./pages/Map'));
const Visits = lazy(() => import('./pages/Visits'));
const RoutesPage = lazy(() => import('./pages/Routes'));
const Locations = lazy(() => import('./pages/Locations'));
const Users = lazy(() => import('./pages/Users'));
const Companies = lazy(() => import('./pages/Companies'));
const Reports = lazy(() => import('./pages/Reports'));
const History = lazy(() => import('./pages/History'));
const Vehicles = lazy(() => import('./pages/Vehicles'));
const Fuel = lazy(() => import('./pages/Fuel'));
const TravelOrders = lazy(() => import('./pages/TravelOrders'));
const Vacations = lazy(() => import('./pages/Vacations'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Notifications = lazy(() => import('./pages/Notifications'));
const GpsTracking = lazy(() => import('./pages/GpsTracking'));
const VisitReasons = lazy(() => import('./pages/VisitReasons'));
const Settings = lazy(() => import('./pages/Settings'));
const Profile = lazy(() => import('./pages/Profile'));
const CompanyDetail = lazy(() => import('./pages/CompanyDetail'));
const RoleManagement = lazy(() => import('./pages/RoleManagement'));
const DispatchBoard = lazy(() => import('./pages/DispatchBoard'));
const TaskTemplates = lazy(() => import('./pages/TaskTemplates'));
const InventoryCheckout = lazy(() => import('./pages/InventoryCheckout'));
const MyInventory = lazy(() => import('./pages/MyInventory'));
const CsvImport = lazy(() => import('./pages/CsvImport'));
const Warehouse = lazy(() => import('./pages/Warehouse'));
const PendingInventoryTransfers = lazy(() => import('./pages/PendingInventoryTransfers'));
const TransferFlowSettings = lazy(() => import('./pages/TransferFlowSettings'));
const CrmHub = lazy(() => import('./pages/CrmHub'));
const CrmLocationDetail = lazy(() => import('./pages/CrmLocationDetail'));
const CrmClientDetail = lazy(() => import('./pages/CrmClientDetail'));
const Consumables = lazy(() => import('./pages/Consumables'));
const MyConsumables = lazy(() => import('./pages/MyConsumables'));
const Organization = lazy(() => import('./pages/Organization'));
const DrivingLogs = lazy(() => import('./pages/DrivingLogs'));
const Layout = lazy(() => import('./components/Layout'));
const OffboardingMode = lazy(() => import('./pages/OffboardingMode'));
const MOBILE_MAX = '(max-width: 767px)';

const App: React.FC = () => {
  const { user, loading } = useAuth();
  const { isDark } = useTheme();
  const isMobile = useMediaQuery(MOBILE_MAX);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  if (loading) {
    return (
      <main id="main-content" className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900" aria-label="Učitavanje aplikacije">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </main>
    );
  }

  return (
    <>
      <ToastContainer
        position={isMobile ? 'top-center' : 'top-right'}
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? 'dark' : 'light'}
        style={
          isMobile
            ? {
                top: 'max(12px, env(safe-area-inset-top, 0px))',
                left: 12,
                right: 12,
                width: 'auto',
                maxWidth: '100%',
                paddingLeft: 0,
                paddingRight: 0,
              }
            : undefined
        }
        toastStyle={
          isMobile
            ? {
                marginBottom: 8,
                width: '100%',
                maxWidth: '100%',
                borderRadius: 12,
              }
            : undefined
        }
        limit={5}
      />

      <a className="skip-link" href="#main-content">Preskoči na sadržaj</a>
      <Suspense fallback={<main id="main-content" className="route-loading" role="status">Učitavanje modula…</main>}>
      {/* Posao 34: firma čeka brisanje — administrator firme vidi samo brisanje i izvoz. */}
      {user?.offboarding ? <main id="main-content"><OffboardingMode /></main> : <Routes>
        <Route path="/login" element={!user ? <Login /> : <Navigate to={homeRoute(user)} />} />
        <Route path="/register" element={<Navigate to="/login" />} />
        <Route path="/verify-email" element={<main id="main-content"><VerifyEmail /></main>} />
        <Route path="/forgot-password" element={<main id="main-content"><ForgotPassword /></main>} />
        <Route path="/reset-password" element={<main id="main-content"><ResetPassword /></main>} />

        <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route path="/" element={<Home />} />
          <Route path="/dashboard" element={user?.role === 'technician' ? <Navigate to="/my-day" replace /> : <Dashboard />} />
          <Route path="/my-day" element={<MyDay />} />
          <Route path="/map" element={<Map />} />
          <Route path="/visits" element={<Visits />} />
          <Route path="/routes" element={<RoutesPage />} />
          <Route path="/dispatch" element={<DispatchBoard />} />
          <Route path="/task-templates" element={<TaskTemplates />} />
          <Route path="/my-inventory" element={<MyInventory />} />
          <Route path="/inventory-checkout" element={<InventoryCheckout />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/crm" element={<CrmHub />} />
          <Route path="/crm/locations/:locationId" element={<CrmLocationDetail />} />
          <Route path="/crm/clients/:clientId" element={<CrmClientDetail />} />
          <Route path="/warehouse" element={<Warehouse />} />
          <Route path="/inventory/pending-transfers" element={<PendingInventoryTransfers />} />
          <Route path="/inventory/transfer-flows" element={<TransferFlowSettings />} />
          <Route path="/consumables" element={<Consumables />} />
          <Route path="/my-consumables" element={<MyConsumables />} />
          <Route path="/users" element={<Users />} />
          <Route path="/organization" element={<Organization />} />
          <Route path="/driving-logs" element={<DrivingLogs />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/companies/:id" element={<CompanyDetail />} />
          <Route path="/reports" element={<ReportCatalog />} />
          <Route path="/reports/visits" element={<Reports />} />
          <Route path="/history" element={<History />} />
          <Route path="/vehicles" element={<Vehicles />} />
          <Route path="/fuel" element={<Fuel />} />
          <Route path="/travel-orders" element={<TravelOrders />} />
          <Route path="/vacations" element={<Vacations />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/gps-tracking" element={<GpsTracking />} />
          <Route path="/visit-reasons" element={<VisitReasons />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/role-management" element={<RoleManagement />} />
          <Route path="/management" element={<Navigate to="/companies" replace />} />
          <Route path="/csv-import" element={<CsvImport />} />
        </Route>

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>}
      </Suspense>
    </>
  );
};

export default App;
